import DownloadIcon from "@mui/icons-material/Download";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { apiClient } from "../../../apis/apiClient";
import {
  getStationQrCode,
  regenerateStationQr,
} from "../../../configs/endpoints";
import { useShowToast } from "../../../hooks";

function StationQrModal({ visible, station, closeAction }) {
  const [qrCodeData, setQrCodeData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [confirmRegenerate, setConfirmRegenerate] = useState(false);
  const showToast = useShowToast();

  useEffect(() => {
    if (visible && station) {
      loadQrCode();
    } else {
      setQrCodeData(null);
      setError(null);
    }
  }, [visible, station]);

  const loadQrCode = async () => {
    if (!station?.code) return;

    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get(getStationQrCode(station.code));
      const body = response?.data?.body;
      if (body?.has_qr_code && body?.qr_code_base64) {
        setQrCodeData(body);
      } else if (body?.error === "NO_QR_CODE") {
        setQrCodeData(null);
      } else if (body?.error === "STATION_NOT_FOUND") {
        setError("Station not found.");
        setQrCodeData(null);
      } else if (
        body?.error === "WHITELABEL_NOT_FOUND" ||
        body?.error === "WHITELABEL_NOT_CONFIGURED"
      ) {
        setError("Whitelabel not configured for this station.");
        setQrCodeData(null);
      } else {
        setQrCodeData(null);
      }
    } catch (err) {
      setError("Failed to load QR code. Please try again.");
      setQrCodeData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (!qrCodeData?.qr_code_base64) return;

    const link = document.createElement("a");
    link.href = `data:image/png;base64,${qrCodeData.qr_code_base64}`;
    link.download = `station-qr-${station.code}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    showToast("QR code downloaded", "success");
  };

  const handleRegenerateConfirm = () => {
    setConfirmRegenerate(true);
  };

  const handleRegenerateCancel = () => {
    setConfirmRegenerate(false);
  };

  const handleRegenerate = async () => {
    setConfirmRegenerate(false);
    setLoading(true);
    setError(null);
    try {
      await apiClient.post(regenerateStationQr(station.code));
      showToast("QR code regenerated successfully", "success");
      await loadQrCode();
    } catch (err) {
      setError("Failed to regenerate QR code. Please try again.");
      showToast("Failed to regenerate QR code", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setQrCodeData(null);
    setError(null);
    closeAction();
  };

  const renderContent = () => {
    if (loading) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <Box sx={{ textAlign: "center", py: 2 }}>
          <Typography color="error">{error}</Typography>
        </Box>
      );
    }

    if (qrCodeData?.qr_code_base64) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
          <img
            src={`data:image/png;base64,${qrCodeData.qr_code_base64}`}
            alt={`QR Code for ${station?.code}`}
            style={{
              maxWidth: "100%",
              height: "auto",
              border: "1px solid #ddd",
              padding: 10,
            }}
          />
        </Box>
      );
    }

    return (
      <Box sx={{ textAlign: "center", py: 2 }}>
        <Typography color="text.secondary">
          No QR code available for this station.
        </Typography>
      </Box>
    );
  };

  const renderActions = () => {
    if (loading) return null;

    if (error) {
      return (
        <>
          <Button onClick={loadQrCode}>Retry</Button>
          <Button onClick={handleClose}>Close</Button>
        </>
      );
    }

    if (qrCodeData?.qr_code_base64) {
      return (
        <>
          <Button startIcon={<DownloadIcon />} onClick={handleDownload}>
            Download
          </Button>
          <Button
            color="warning"
            startIcon={<RefreshIcon />}
            onClick={handleRegenerateConfirm}
          >
            Regenerate
          </Button>
          <Button variant="contained" onClick={handleClose}>
            Close
          </Button>
        </>
      );
    }

    return (
      <>
        <Button variant="contained" onClick={handleRegenerate}>
          Generate QR Code
        </Button>
        <Button onClick={handleClose}>Close</Button>
      </>
    );
  };

  return (
    <>
      <Dialog
        open={visible}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth={true}
      >
        <DialogTitle>
          Station QR Code - {station?.name || station?.code}
        </DialogTitle>
        <DialogContent>{renderContent()}</DialogContent>
        <DialogActions>{renderActions()}</DialogActions>
      </Dialog>

      <Dialog open={confirmRegenerate} onClose={handleRegenerateCancel}>
        <DialogTitle>Confirm QR Code Regeneration</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to regenerate the QR code? This will
            invalidate the current QR code. Any printed codes will stop working.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRegenerateCancel}>Cancel</Button>
          <Button
            onClick={handleRegenerate}
            color="warning"
            variant="contained"
          >
            Regenerate
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default StationQrModal;
